import { useQueries, useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import React, { useEffect, useMemo } from 'react';

import CategoriesPage from '../../components/pages/v2/categories';
import { translateSlugs } from '../../services/language/slug_translation';
import {
  ADVANCED_VIEW_ACTIVE,
  DETAILED_PRICES,
  SHIPPING_LOCATION,
} from '../../static/cookies';
import getCategoryNames from '../../utils/get-category-names';
import { isGlobalUser } from '../../utils/is-global-user';
import { isUKuser } from '../../utils/is-uk-user';
import { isUSuser } from '../../utils/is-us-user';
import getGroups from '../../utils/react-query/query-options/get-groups';
import getGroupsWithStocks from '../../utils/react-query/query-options/get-groups-with-stock';
import getLasEntries from '../../utils/react-query/query-options/get-last-entries';
import getLastVehicles from '../../utils/react-query/query-options/get-last-vehicles';
import getLoggedUser from '../../utils/react-query/query-options/get-logged-user';
import getStock from '../../utils/react-query/query-options/get-stocks';
import getTranslatedSlugs from '../../utils/react-query/query-options/get-translated-slugs';
import getVehicle from '../../utils/react-query/query-options/get-vehicle';
import { redirectForAdvanced } from '../../utils/redirect-for-advanced';
import slugChange301 from '../../utils/slug-change-301';
import { useAppContext } from '../../components/providers/app-context';
import { getSortedGroups } from '../../utils/get-sorted-groups';
import forceShippingUKForCoUK from '../../utils/force-shipping-uk-for-co-uk';

const useData = () => {
  const {
    locale = 'en',
    route,
    query: { category: queryCategory, slug: querySlug },
  } = useRouter();
  const { detailedPrice, shippingLocation } = useAppContext();
  const isUK = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');
  const isEnglish = isUK || locale === 'en';

  const { vehicle, category } = useMemo(() => {
    if (
      route === '/auto-parts/[category]/category/[slug]' ||
      route === '/auto-parts/[category]/categories'
    ) {
      return {
        vehicle: queryCategory,
        category: querySlug,
      };
    }

    return {
      category: queryCategory,
      vehicle: querySlug,
    };
  }, [queryCategory, querySlug, route]);

  const hasSelection = !!(queryCategory && querySlug);
  const selection = hasSelection
    ? {
      category: { queryCategory, querySlug },
    }
    : undefined;

  const queries = useMemo(
    () => ({
      queries: [
        vehicle
          ? getGroupsWithStocks({
            locale,
            car_identifier: vehicle as string,
            ...(shippingLocation
              ? { shipping_location: shippingLocation }
              : {}),
          })
          : getGroups({ locale }),
        getLasEntries({
          locale,
          part: '',
          detailed_prices: !!detailedPrice,
          ...(vehicle && { vehicle: vehicle as string }),
          ...(isEnglish && { page: vehicle ? undefined : 'catalogue' })
        }),
        getLastVehicles({
          locale,
          ...(vehicle && { vehicle: vehicle as string }),
          ...(isEnglish && { page: vehicle ? undefined : 'catalogue' })
        }),
      ].map(([key, fn]) => ({
        queryKey: key,
        queryFn: fn,
      })),
    }),
    [locale, vehicle]
  );

  const [{ data: groups }, lastEntriesObject, vehiclesObject] =
    useQueries(queries);

  const lastEntries = lastEntriesObject?.data;
  const isFetchedLastEntries = lastEntriesObject?.isFetched;
  const vehicles = vehiclesObject?.data;

  const info = useMemo(
    () =>
      selection?.category ? getCategoryNames(selection.category, groups) : null,
    [selection, groups]
  );

  // GTM
  useEffect(() => {
    if (isFetchedLastEntries && lastEntries.length > 0) {
      // @ts-ignore
      window.dataLayer.push({
        event: 'eec.impressionView',
        ecommerce: {
          currencyCode: 'EUR',
          impressions: lastEntries?.map(
            (item: TProductListItem, index: number) => ({
              list: 'Last Parts in Stock', // List name where the impression was viewed
              id: item.sku, // SKU of the product
              name: item.part_en, // name of the product.
              brand: item.car_brand, // car brand of the part
              category: item.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
              price: item.pricing?.price_product
                ? Number(item.pricing.price_product).toFixed(2)
                : null, // price of the part without shipping, tax and currency
              position: index, // the position of the item in the list
              dimension2: item.reference, // reference of the product
              dimension3: item.car_model, // car model of the part
              dimension4: item.car_version, // car version of the part
            })
          ),
        },
      });

      // GA4
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          currency: 'EUR', // Currency of the items associated with the event, in 3-letter ISO 4217 format
          items: lastEntries?.map((item: TProductListItem, index: number) => ({
            item_list_name: 'Last Parts in Stock', // List name where the impression was viewed
            item_list_id: 'last-parts-in-stock', // List name where the impression was viewed
            item_id: item.sku, // SKU of the product
            item_name: item.part_en, // name of the product.
            item_brand: item.car_brand, // car brand of the part
            item_category: item.category_en, // Product category(ies) - each "/" is a category level (max 5 levels)
            item_category2: item.part_en, // part name === product name
            price: item.pricing?.price_product
              ? Number(item.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            index: index, // the position of the item in the list
            quantity: '1', // Hardcoded to 1
          })),
        },
      });
    }
  }, [lastEntries, isFetchedLastEntries]);

  const { data: vehicleData } = useQuery(...getVehicle({ locale, vehicle }), {
    enabled: !!vehicle,
  });

  const { data: stock } = vehicle
    ? useQuery(
      ...getStock({
        locale,
        no_round: false,
        stocks: [
          {
            vehicle: vehicle,
          },
          {
            vehicle: vehicleData?.info?.brand.slug,
          },
        ],
      })
    )
    : { data: [] };

  return {
    groups,
    ...(vehicle && { carInfo: vehicleData?.info }),
    lastEntries,
    vehicles,
    vehicle,
    category,
    stock,
    info,
  };
};

export default function Categories({ groupsSort }) {
  const { groups, ...data } = useData();

  return (
    <main>
      <CategoriesPage {...data} groups={groupsSort} />
    </main>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en',
  req,
  res,
  query,
  params,
  resolvedUrl,
}) => {
  isUSuser(req, res);
  isUKuser(req, res);
  isGlobalUser(req, res);
  await forceShippingUKForCoUK({ req, res, locale });

  const { category: vehicle = '' } = query;
  const { default: getGenericClient } = await import('../../server/client');
  const genericClient = getGenericClient();
  const isUK = process.env.NEXT_PUBLIC_HOST?.includes('.co.uk');
  const isEnglish = isUK || locale === 'en';

  try {
    const [user, groups, vehicleData] = await Promise.all([
      genericClient.fetchQuery(...getLoggedUser({ locale }, req)),
      genericClient.fetchQuery(
        ...getGroupsWithStocks(
          {
            locale,
            ...(vehicle && { car_identifier: vehicle as string }),
            ...(req.cookies[SHIPPING_LOCATION]
              ? {
                shipping_location: parseInt(
                  req.cookies[SHIPPING_LOCATION],
                  10
                ),
              }
              : {}),
          },
          req
        )
      ),
      vehicle
        ? genericClient.fetchQuery(
          ...getVehicle(
            {
              locale,
              vehicle,
            },
            req
          )
        )
        : null,
      genericClient.prefetchQuery(
        getTranslatedSlugs(
          {
            csrftoken: req.cookies['csrftoken'] || '',
            lang_origin: locale,
            lang_trans: null,
            slugs: [
              {
                slug_origin: vehicle as string,
                slug_type: 'category',
              },
            ],
          },
          req
        )
      ),
      (isEnglish || (!isEnglish && vehicle)) ? genericClient.prefetchQuery(
        ...getLasEntries(
          {
            locale,
            part: '',
            detailed_prices: req.cookies[DETAILED_PRICES] === 'true',
            ...(isEnglish && { page: vehicle ? undefined : 'catalogue' }),
            ...(vehicle && { vehicle: vehicle as string || '' })
          },
          req
        )
      ) : null,
      (isEnglish || (!isEnglish && vehicle)) ? genericClient.prefetchQuery(
        ...getLastVehicles(
          {
            locale,
            part: '',
            ...(isEnglish && { page: vehicle ? undefined : 'catalogue' }),
            ...(vehicle && { vehicle: vehicle as string || '' })
          },
          req
        )
      ) : null,
    ]);

    if (vehicle) {
      await genericClient.prefetchQuery(
        ...getStock(
          {
            locale,
            stocks: [
              {
                vehicle: vehicle,
              },
              {
                vehicle: vehicleData?.info?.brand.slug,
              },
            ],
          },
          req
        )
      );
    }

    let groupsSort: TCategories = [];
    if (groups) {
      groupsSort = getSortedGroups(groups);
    }

    const { default: retrieveStateAndClear } = await import(
      '../../utils/retrieve-state-and-clear'
    );

    if (
      user?.staff?.is_pro &&
      !!parseInt(req.cookies[ADVANCED_VIEW_ACTIVE] || '0')
    ) {
      return redirectForAdvanced({
        locale,
        vehicle: vehicle as string,
      });
    }

    return {
      props: {
        dehydratedState: retrieveStateAndClear(genericClient),
        shippingLocation: parseInt(req.cookies[SHIPPING_LOCATION], 10),
        detailPrices: req.cookies[DETAILED_PRICES] || null,
        groupsSort,
      },
    };
  } catch (e) {
    if (e.status === 301) {
      const newQuery: { [key: string]: string } = {
        ...params,
        ...((e.data?.version || e.data?.model) && {
          category: e.data?.version || e.data?.model,
        }),
      };

      return slugChange301({
        locale,
        query: newQuery,
        res,
        page: params?.category ? 'categories-with-vehicle' : 'categories',
      });
    } else if (e.status === 404) {
      return {
        notFound: true,
      };
    }

    console.log(e);
    throw e;
  }
};
